// React
import React, { useState, useEffect, useCallback, useRef } from "react";

// Third-party Hooks
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDashboardPath } from "../hooks/useDashboardNavigate";
import { motion, AnimatePresence } from "framer-motion";
import { ROUTE_PATHS } from "../config/routes";

// Services
import { auth } from "../services/api";

// Context
import { useAuth } from "../context/AuthContext";

// Components
import OtpInput from "../components/forms/OtpInput";
import { Logo } from "../components/Logo";
import { OmidexTrainingButton } from "../components/common/OmidexTrainingModal";

// Chakra UI Components & Hooks
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  useColorModeValue,
  Icon,
  HStack,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  InputGroup,
  InputRightElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

type LoginStep = 0 | 1 | 2; // 0: National ID, 1: OTP, 2: Success
type PersonType = "private" | "legal";

type CaptchaChallenge = {
  id: string;
  svg: string;
};

export const LoginPage = () => {
  const [step, setStep] = useState<LoginStep>(0);
  const [personType, setPersonType] = useState<PersonType>("private");
  const [isLoading, setIsLoading] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [agreementOpen, setAgreementOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { login } = useAuth();
  const redirectTo = searchParams.get("redirectTo");
  const impersonationToken = searchParams.get("impersonationToken");
  const referralCodeFromUrl = searchParams.get("ref");
  const lastConsumedImpersonationTokenRef = useRef<string | null>(null);

  const inputBg = useColorModeValue("light.600", "dark.600");
  const loginBg = useColorModeValue("white", "dark.50");
  const inputBorder = useColorModeValue("gray.200", "gray.700");
  const inputHoverBorder = useColorModeValue("brand.400", "brand.300");
  const inputFocusBorder = useColorModeValue("brand.500", "brand.400");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const successColor = useColorModeValue("green.500", "green.300");
  const subHeadingColor = useColorModeValue("brand.500", "brand.400");
  const tabGroupBg = useColorModeValue("gray.100", "whiteAlpha.120");
  const activeTabBg = useColorModeValue("white", "whiteAlpha.180");
  const activeTabColor = useColorModeValue("brand.600", "white");
  const inactiveTabColor = useColorModeValue("gray.500", "gray.300");
  const tabShadow = useColorModeValue(
    "0 10px 25px rgba(15, 23, 42, 0.08)",
    "0 10px 25px rgba(0, 0, 0, 0.22)",
  );
  const identifierLabel = personType === "private" ? "کد ملی" : "شناسه ملی";
  const identifierMaxLength = personType === "private" ? 10 : 11;
  const referralHint = referralCodeFromUrl
    ? "کد معرفی از لینک دعوت شما ثبت شده است."
    : "در صورت داشتن کد معرفی، آن را وارد کنید.";

  const loadCaptcha = useCallback(async () => {
    try {
      setIsCaptchaLoading(true);
      const response = await auth.getCaptcha();
      setCaptcha(response.result);
      setCaptchaAnswer("");
      setCaptchaError("");
    } catch (error) {
      console.error("Captcha fetch error:", error);
      toast({
        title: "خطا در دریافت کپچا",
        description: "لطفا دوباره تلاش کنید",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsCaptchaLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (impersonationToken) {
      return;
    }
    loadCaptcha();
  }, [impersonationToken, loadCaptcha]);

  useEffect(() => {
    if (referralCodeFromUrl && !referralCode) {
      setReferralCode(referralCodeFromUrl);
    }
  }, [referralCode, referralCodeFromUrl]);

  useEffect(() => {
    if (!impersonationToken) {
      lastConsumedImpersonationTokenRef.current = null;
      return;
    }

    if (lastConsumedImpersonationTokenRef.current === impersonationToken) {
      return;
    }

    lastConsumedImpersonationTokenRef.current = impersonationToken;

    const consumeImpersonationLink = async () => {
      try {
        setIsLoading(true);
        const response =
          await auth.consumeImpersonationLink(impersonationToken);
        login(response.token, response.user);
        navigate(
          redirectTo ||
            getDashboardPath(response.user?.role?.permissions ?? []),
          { replace: true },
        );
      } catch (error: any) {
        toast({
          title: "خطا در استفاده از لینک ورود",
          description:
            error?.response?.data?.message || error?.message || "خطای ناشناخته",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
        navigate(
          redirectTo
            ? `${ROUTE_PATHS.LOGIN}?redirectTo=${encodeURIComponent(redirectTo)}`
            : ROUTE_PATHS.LOGIN,
          { replace: true },
        );
      } finally {
        setIsLoading(false);
      }
    };

    void consumeImpersonationLink();
  }, [impersonationToken, login, navigate, redirectTo, toast]);

  // Radial gradient with multiple positions for light and dark themes
  const bgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249, 168, 166, 0.5), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249, 168, 166, 0.5), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173, 11, 6, 0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173, 11, 6, 0.22), transparent 50%)",
  );

  const bgColor = useColorModeValue("white", "dark.900");

  // Countdown timer for OTP resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Step 2: Verify OTP
  const handleOtpSubmit = useCallback(async () => {
    if (otp.length !== 5) {
      setOtpError("کد تایید باید 5 رقم باشد");
      return;
    }

    try {
      setIsLoading(true);
      setOtpError("");
      const response = await auth.verifyOtp(
        identifier,
        otp,
        personType,
        referralCode.trim() || undefined,
      );

      if (response?.token) {
        login(response.token, response.user);
        setStep(2);

        // Auto-redirect after 1.5 seconds
        setTimeout(() => {
          navigate(
            redirectTo ||
              getDashboardPath(response.user?.role?.permissions ?? []),
          );
        }, 1500);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("OTP verification error:", error);
      setOtpError("کد تایید نامعتبر است");
      setOtp(""); // Clear OTP input
      toast({
        title: "خطا در تایید کد",
        description: error.response?.data?.message || "کد تایید نامعتبر است",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    identifier,
    login,
    navigate,
    otp,
    personType,
    redirectTo,
    referralCode,
    toast,
  ]);

  // Auto-submit OTP when complete
  useEffect(() => {
    if (otp.length === 5 && step === 1) {
      handleOtpSubmit();
    }
  }, [handleOtpSubmit, otp, step]);

  // Validate national ID format
  const validateIdentifier = (value: string): boolean => {
    if (!value) {
      setIdentifierError(`${identifierLabel} الزامی است`);
      return false;
    }
    if (personType === "private" && !/^\d{10}$/.test(value)) {
      setIdentifierError("کد ملی باید 10 رقم باشد");
      return false;
    }
    if (personType === "legal" && !/^\d{11}$/.test(value)) {
      setIdentifierError("شناسه ملی باید 11 رقم باشد");
      return false;
    }
    setIdentifierError("");
    return true;
  };

  const validateCaptcha = (value: string): boolean => {
    if (!captcha?.id) {
      setCaptchaError("کپچا در دسترس نیست، دوباره تلاش کنید");
      return false;
    }

    if (!value.trim()) {
      setCaptchaError("پاسخ کپچا الزامی است");
      return false;
    }

    setCaptchaError("");
    return true;
  };

  // Step 1: Request OTP
  const handleNationalIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateIdentifier(identifier) || !validateCaptcha(captchaAnswer)) {
      return;
    }

    try {
      setIsLoading(true);
      await auth.requestOtp(
        identifier,
        personType,
        captcha!.id,
        captchaAnswer.trim(),
      );

      toast({
        title: "کد تایید ارسال شد",
        description: "کد تایید به شماره موبایل شما ارسال شد",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });

      setStep(1);
      setResendCountdown(60); // 60 seconds countdown
      void loadCaptcha();
    } catch (error: any) {
      console.error("OTP request error:", error);
      await loadCaptcha();
      toast({
        title: "خطا در ارسال کد",
        description: error.response?.data?.message || "خطا در ارسال کد تایید",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setOtp("");
    setOtpError("");

    try {
      setIsLoading(true);
      await auth.requestOtp(identifier, personType);

      toast({
        title: "کد تایید مجدد ارسال شد",
        description: "کد تایید جدید به شماره موبایل شما ارسال شد",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });

      setResendCountdown(60);
    } catch (error: any) {
      console.error("OTP resend error:", error);
      toast({
        title: "خطا در ارسال مجدد",
        description: error.response?.data?.message || "خطا در ارسال کد تایید",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit national ID (go back to step 0)
  const handleEditNationalId = () => {
    setStep(0);
    setOtp("");
    setOtpError("");
    setResendCountdown(0);
    void loadCaptcha();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      x: -50,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <Box
      id="hero"
      position="relative"
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent={"center"}
      bg={bgColor}
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: bgGradient,
        zIndex: 0,
      }}
    >
      <Box
        p={8}
        width="100%"
        maxW="450px"
        minH="420px"
        borderRadius="xl"
        position="relative"
        zIndex="2"
        mx={4}
        bg={loginBg}
        boxShadow={"xl"}
      >
        <VStack spacing={6} align="center" h="full">
          {/* Logo */}
          <Logo variant="vertical" w="100px" mx="auto" to="/" />

          {/* Multi-Step Form */}
          <Box w="full" flex="1" display="flex" flexDirection="column">
            <AnimatePresence mode="wait">
              {/* Step 0: National ID Input */}
              {step === 0 && (
                <MotionBox
                  key="step-0"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  w="full"
                >
                  <form onSubmit={handleNationalIdSubmit}>
                    <MotionVStack spacing={4}>
                      <Tabs
                        w="full"
                        index={personType === "private" ? 0 : 1}
                        onChange={(index) => {
                          setPersonType(index === 0 ? "private" : "legal");
                          setIdentifier("");
                          setIdentifierError("");
                        }}
                        variant="line"
                      >
                        <TabList>
                          <Tab flex={1}>اشخاص حقیقی</Tab>
                          <Tab flex={1}>اشخاص حقوقی</Tab>
                        </TabList>

                        <TabPanels>
                          <TabPanel px={0} pt={4} pb={0}>
                            <FormControl isInvalid={!!identifierError}>
                              <FormLabel
                                color={textColor}
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                کد ملی
                              </FormLabel>
                              <Input
                                type="text"
                                placeholder="کد ملی 10 رقمی خود را وارد کنید"
                                size="lg"
                                fontSize={"md"}
                                textAlign="center"
                                h="50px"
                                borderRadius={"lg"}
                                bg={inputBg}
                                borderColor={inputBorder}
                                value={identifier}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /\D/g,
                                    "",
                                  );
                                  if (value.length <= 10) {
                                    setIdentifier(value);
                                    if (identifierError) {
                                      setIdentifierError("");
                                    }
                                  }
                                }}
                                _hover={{ borderColor: inputHoverBorder }}
                                _focus={{
                                  borderColor: inputFocusBorder,
                                  boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                                }}
                                maxLength={10}
                                autoFocus
                              />
                              <FormErrorMessage>
                                {identifierError}
                              </FormErrorMessage>
                            </FormControl>
                          </TabPanel>

                          <TabPanel px={0} pt={4} pb={0}>
                            <FormControl isInvalid={!!identifierError}>
                              <FormLabel
                                color={textColor}
                                fontSize="sm"
                                fontWeight="medium"
                              >
                                شناسه ملی
                              </FormLabel>
                              <Input
                                type="text"
                                placeholder="شناسه ملی 11 رقمی شرکت را وارد کنید"
                                size="lg"
                                fontSize={"md"}
                                textAlign="center"
                                h="50px"
                                borderRadius={"lg"}
                                bg={inputBg}
                                borderColor={inputBorder}
                                value={identifier}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /\D/g,
                                    "",
                                  );
                                  if (value.length <= 11) {
                                    setIdentifier(value);
                                    if (identifierError) {
                                      setIdentifierError("");
                                    }
                                  }
                                }}
                                _hover={{ borderColor: inputHoverBorder }}
                                _focus={{
                                  borderColor: inputFocusBorder,
                                  boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                                }}
                                maxLength={11}
                                autoFocus
                              />
                              <FormErrorMessage>
                                {identifierError}
                              </FormErrorMessage>
                            </FormControl>
                          </TabPanel>
                        </TabPanels>
                      </Tabs>

                      <FormControl>
                        <FormLabel
                          color={textColor}
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          کد معرفی
                        </FormLabel>
                        <Input
                          type="text"
                          placeholder="اختیاری"
                          size="lg"
                          fontSize="md"
                          textAlign="center"
                          h="50px"
                          borderRadius="lg"
                          bg={inputBg}
                          borderColor={inputBorder}
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value)}
                          _hover={{ borderColor: inputHoverBorder }}
                          _focus={{
                            borderColor: inputFocusBorder,
                            boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                          }}
                          dir="ltr"
                        />
                        <Text mt={2} fontSize="xs" color={textColor}>
                          {referralHint}
                        </Text>
                      </FormControl>

                      <FormControl isInvalid={!!captchaError}>
                        <FormLabel
                          color={textColor}
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          بررسی امنیتی
                        </FormLabel>
                        <InputGroup size="lg">
                          <Input
                            type="text"
                            placeholder="حروف تصویر را وارد کنید"
                            fontSize="md"
                            textAlign="center"
                            h="50px"
                            borderRadius="lg"
                            bg={inputBg}
                            borderColor={inputBorder}
                            value={captchaAnswer}
                            onChange={(e) => {
                              setCaptchaAnswer(
                                e.target.value.replace(/[^a-zA-Z0-9]/g, ""),
                              );
                              if (captchaError) {
                                setCaptchaError("");
                              }
                            }}
                            _hover={{ borderColor: inputHoverBorder }}
                            _focus={{
                              borderColor: inputFocusBorder,
                              boxShadow: `0 0 0 1px ${inputFocusBorder}`,
                            }}
                            isDisabled={isCaptchaLoading}
                          />
                          <InputRightElement h="50px" width="5rem">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                void loadCaptcha();
                              }}
                              isLoading={isCaptchaLoading}
                              aria-label="refresh captcha"
                            >
                              بازخوانی
                            </Button>
                          </InputRightElement>
                        </InputGroup>
                        <Box
                          mt={3}
                          minH="72px"
                          borderRadius="lg"
                          border="1px solid"
                          borderColor={inputBorder}
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          overflow="hidden"
                        >
                          {captcha?.svg ? (
                            <Box
                              w="full"
                              sx={{
                                svg: {
                                  width: "100%",
                                  height: "70px",
                                  display: "block",
                                },
                              }}
                              dangerouslySetInnerHTML={{ __html: captcha.svg }}
                            />
                          ) : (
                            <Text fontSize="sm" color={textColor}>
                              در حال دریافت کپچا...
                            </Text>
                          )}
                        </Box>
                        <FormErrorMessage>{captchaError}</FormErrorMessage>
                      </FormControl>

                      <Button
                        type="submit"
                        size="lg"
                        width="100%"
                        h="55px"
                        borderRadius={"lg"}
                        isLoading={isLoading}
                        loadingText="در حال ارسال..."
                        mt={4}
                        isDisabled={
                          identifier.length !== identifierMaxLength ||
                          !captcha?.id ||
                          !captchaAnswer.trim()
                        }
                      >
                        دریافت کد تایید
                      </Button>

                      <Button as={Link} href="/" variant="ghost" width="100%">
                        بازگشت به صفحه اصلی
                      </Button>

                      <OmidexTrainingButton width="100%" />
                    </MotionVStack>
                  </form>
                </MotionBox>
              )}

              {/* Step 1: OTP Input */}
              {step === 1 && (
                <MotionBox
                  key="step-1"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  w="full"
                >
                  <VStack spacing={4}>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color={textColor}
                      textAlign="center"
                    >
                      کد تایید را وارد کنید
                    </Text>

                    <Text fontSize="sm" color={textColor} textAlign="center">
                      کد 5 رقمی ارسال شده برای {identifierLabel.toLowerCase()}{" "}
                      خود را وارد کنید
                    </Text>

                    <Box w="full" mt={4}>
                      <OtpInput
                        value={otp}
                        onChange={setOtp}
                        isDisabled={isLoading}
                        isInvalid={!!otpError}
                        errorMessage={otpError}
                        label=""
                      />
                    </Box>

                    {/* Resend & Edit buttons */}
                    <HStack spacing={3} w="full" mt={6}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEditNationalId}
                        isDisabled={isLoading}
                        flex={1}
                      >
                        ویرایش کد ملی
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResendOtp}
                        isDisabled={resendCountdown > 0 || isLoading}
                        flex={1}
                      >
                        {resendCountdown > 0
                          ? `ارسال مجدد (${resendCountdown})`
                          : "ارسال مجدد کد"}
                      </Button>
                    </HStack>

                    <Button
                      size="lg"
                      width="100%"
                      h="55px"
                      borderRadius={"lg"}
                      isLoading={isLoading}
                      loadingText="در حال بررسی..."
                      mt={2}
                      onClick={handleOtpSubmit}
                      isDisabled={otp.length !== 5}
                    >
                      تایید و ورود
                    </Button>
                  </VStack>
                </MotionBox>
              )}

              {/* Step 2: Success */}
              {step === 2 && (
                <MotionBox
                  key="step-2"
                  variants={successVariants}
                  initial="hidden"
                  animate="visible"
                  w="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minH="300px"
                >
                  <VStack spacing={4}>
                    <Icon
                      verticalAlign="middle"
                      as={CheckCircleIcon}
                      w={20}
                      h={20}
                      color={successColor}
                    />
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color={successColor}
                      textAlign="center"
                    >
                      ورود موفق!
                    </Text>
                    <Text fontSize="md" color={textColor} textAlign="center">
                      در حال انتقال به پنل سرمایه گذار...
                    </Text>
                  </VStack>
                </MotionBox>
              )}
            </AnimatePresence>

            <Text fontSize="xs" color={textColor} textAlign="center" mt={5}>
              ورود شما به منزله خواندن و پذیرش{" "}
              <Link
                color={subHeadingColor}
                fontWeight="semibold"
                onClick={(e) => {
                  e.preventDefault();
                  setAgreementOpen(true);
                }}
                cursor="pointer"
                textDecoration="underline"
              >
                قوانین و مقررات و توافق نامه
              </Link>{" "}
              می باشد
            </Text>
          </Box>
        </VStack>
      </Box>

      <Modal
        isOpen={agreementOpen}
        onClose={() => setAgreementOpen(false)}
        isCentered
        scrollBehavior="inside"
        size="2xl"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4} pt="40px">
          <ModalHeader
            fontSize="md"
            borderBottom="1px solid"
            borderColor={inputBorder}
          >
            قوانین و مقررات و توافق نامه
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5} fontSize="sm" color={textColor} lineHeight="tall">
            <VStack spacing={3} align="stretch">
              <Text fontWeight="bold" color={subHeadingColor}>
                توافقنامه احراز هویت و اعطای مجوز انجام معامله به نمایندگی
              </Text>
              <Text>
                لطفاً پیش از دریافت کد احراز هویت (OTP) مفاد زیر را با دقت
                مطالعه کنید. با انتخاب گزینه «تأیید و دریافت کد»، شما اعلام
                می‌کنید که تمامی مفاد این توافقنامه را خوانده، فهمیده و با آن
                موافقت می‌کنید:
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                احراز هویت از طریق سجام:
              </Text>
              <Text>
                اینجانب اجازه می‌دهم «تأمین سرمایه امید» با استفاده از اطلاعات
                ثبت‌شده در سامانهٔ سجام، برای من کد احراز هویت (OTP) ارسال نماید
                تا هویت و مالکیت من در این فرایند به‌صورت رسمی تأیید شود.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                اعطای مجوز و نمایندگی انجام معامله:
              </Text>
              <Text>
                با پذیرش این توافقنامه، به «تأمین سرمایه امید» به‌عنوان نهاد
                مالی دارای مجوز از سازمان بورس و اوراق بهادار، اجازه می‌دهم به
                نمایندگی از من و بر اساس درخواست ثبت‌شده، اقدام به انجام معاملات
                خرید یا فروش اوراق بهادار یا سایر دارایی‌های مالی در سامانه‌های
                معاملاتی نماید.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                حدود اختیار:
              </Text>
              <Text>
                این نمایندگی صرفاً برای همان معامله‌ای است که من با ثبت سفارش
                مشخص کرده‌ام (از جمله مبلغ، نوع دارایی و زمان اجرا) و هیچ اختیار
                اضافی یا دائمی برای انجام معاملات دیگر ایجاد نمی‌کند.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                استفاده و نگهداری اطلاعات:
              </Text>
              <Text>
                اطلاعات هویتی و مالی من صرفاً برای اجرای معامله، احراز هویت، ثبت
                سوابق و رعایت الزامات قانونی و مقررات مبارزه با پولشویی
                (AML/CFT) استفاده خواهد شد.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                ثبت الکترونیکی و مستندات:
              </Text>
              <Text>
                کلیهٔ اقدامات مرتبط با این درخواست شامل زمان، آدرس IP، شناسه
                دستگاه و کد پیگیری به‌صورت دیجیتال ثبت می‌شود و این سوابق مبنای
                رسمی هرگونه بررسی یا استناد حقوقی خواهد بود.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                مسئولیت و پیگیری:
              </Text>
              <Text>
                پس از ارسال OTP و انجام معامله، درخواست من قطعی و قابل استناد
                تلقی می‌شود. هرگونه ادعای بعدی مبنی بر «عدم آگاهی»، «عدم انجام
                معامله» یا «عدم رضایت» بدون ارائه مستندات معتبر قابل بررسی
                نخواهد بود.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                همکاری در بررسی‌های قانونی:
              </Text>
              <Text>
                متعهد می‌شوم در صورت نیاز نهادهای ناظر یا سازمان بورس، همکاری
                لازم جهت ارائه مدارک و اطلاعات تکمیلی را انجام دهم.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                مقررات نهایی:
              </Text>
              <Text>
                کلیهٔ معاملات انجام‌شده در چارچوب این توافقنامه تابع قوانین و
                مقررات جاری جمهوری اسلامی ایران و ضوابط سازمان بورس و اوراق
                بهادار است.
              </Text>
              <Text fontWeight="bold" color={subHeadingColor}>
                با انتخاب گزینه «تأیید و دریافت کد»:
              </Text>
              <Text>
                من صحت اطلاعات خود را تأیید می‌کنم؛ با ارسال کد احراز هویت (OTP)
                از طریق سجام موافقم؛ و به «تأمین سرمایه امید» اجازه می‌دهم در
                حدود مشخص‌شده، معامله مورد نظر را به نمایندگی از من ثبت و اجرا
                نماید.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
